import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DataTable from '../components/DataTable';
import { ArrowLeft, ChevronDown, Plus, Edit, Check, X } from 'lucide-react';
const AlertDetail: React.FC = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'users' | 'accounts'>('users');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const descriptionInputRef = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Mock alert data with Distru-specific content
  const [alert, setAlert] = useState({
    id: parseInt(id || '1'),
    name: 'Potential Analytics Upsell (High Manual Reporting)',
    description: 'Flags accounts doing heavy manual analysis, suggesting they\'re ready for the "Advanced Analytics" package.',
    category: 'Upsell & Expansion',
    status: 'Active',
    lastRun: '2 hours ago',
    createdAt: 'Jan 15, 2023',
    createdBy: 'Jared Smith',
    suggestedAction: 'Schedule Advanced Analytics demo',
    matches: 5,
    filters: [{
      id: 1,
      color: 'blue',
      action: 'Account',
      condition: 'Property',
      property: 'plan',
      operator: '!=',
      value: 'Advanced Analytics'
    }, {
      id: 2,
      color: 'green',
      action: 'Report Exported',
      condition: 'Count',
      qualifier: 'all types',
      operator: '>',
      value: '20',
      timeframe: '30 days'
    }]
  });
  // Sample user actions for dropdowns
  const userActions = ['Sales Order Created', 'Sales Order Updated', 'Sales Order Fulfilled', 'Invoice Created', 'Invoice Sent', 'Payment Received', 'Purchase Order Created', 'Purchase Order Received', 'Transfer Manifest Created', 'E-commerce Menu Used', 'Inventory Adjusted', 'New Package Created', 'Report Exported', 'Report Viewed', 'Brand Filter Used'];
  // Sample operators for dropdowns
  const operators = ['=', '!=', '>', '<', '>=', '<=', 'contains', 'starts with', 'ends with'];
  // Sample timeframes for dropdowns
  const timeframes = ['24 hours', '7 days', '14 days', '30 days', '90 days'];
  // User columns and data
  const userColumns = [{
    key: 'name',
    label: 'Name',
    sortable: true
  }, {
    key: 'email',
    label: 'Email',
    sortable: true
  }, {
    key: 'account',
    label: 'Account',
    sortable: true
  }, {
    key: 'reportCount',
    label: 'Reports Exported',
    sortable: true
  }, {
    key: 'lastActive',
    label: 'Last Active',
    sortable: true
  }];
  const userData = [{
    id: 1,
    name: 'Sarah Johnson',
    email: 'sarah.j@shopmajorbloom.com',
    account: 'Shop Major Bloom',
    reportCount: 28,
    lastActive: '2 days ago'
  }, {
    id: 2,
    name: 'Michael Chen',
    email: 'michael.c@shopmajorbloom.com',
    account: 'Shop Major Bloom',
    reportCount: 22,
    lastActive: '1 day ago'
  }, {
    id: 3,
    name: 'Emily Rodriguez',
    email: 'emily.r@greenleaf.com',
    account: 'Green Leaf Dispensary',
    reportCount: 32,
    lastActive: '4 hours ago'
  }, {
    id: 4,
    name: 'David Kim',
    email: 'd.kim@greenleaf.com',
    account: 'Green Leaf Dispensary',
    reportCount: 25,
    lastActive: '1 week ago'
  }, {
    id: 5,
    name: 'Jessica Williams',
    email: 'j.williams@purecann.com',
    account: 'Pure Cann',
    reportCount: 35,
    lastActive: '3 days ago'
  }, {
    id: 6,
    name: 'Robert Taylor',
    email: 'r.taylor@purecann.com',
    account: 'Pure Cann',
    reportCount: 21,
    lastActive: '5 hours ago'
  }, {
    id: 7,
    name: 'Amanda Martinez',
    email: 'a.martinez@herbology.com',
    account: 'Herbology',
    reportCount: 27,
    lastActive: '12 hours ago'
  }, {
    id: 8,
    name: 'James Wilson',
    email: 'j.wilson@herbology.com',
    account: 'Herbology',
    reportCount: 24,
    lastActive: '2 days ago'
  }];
  // Account columns and data
  const accountColumns = [{
    key: 'name',
    label: 'Account Name',
    sortable: true
  }, {
    key: 'industry',
    label: 'Type',
    sortable: true
  }, {
    key: 'reportCount',
    label: 'Total Reports',
    sortable: true
  }, {
    key: 'users',
    label: 'Total Users',
    sortable: true
  }, {
    key: 'mrr',
    label: 'MRR',
    sortable: true
  }];
  const accountData = [{
    id: 1,
    name: 'Shop Major Bloom',
    industry: 'Retailer',
    reportCount: 52,
    users: 5,
    mrr: '$1,500'
  }, {
    id: 2,
    name: 'Green Leaf Dispensary',
    industry: 'Retailer',
    reportCount: 65,
    users: 8,
    mrr: '$2,200'
  }, {
    id: 3,
    name: 'Pure Cann',
    industry: 'Manufacturer',
    reportCount: 78,
    users: 6,
    mrr: '$1,800'
  }, {
    id: 4,
    name: 'Herbology',
    industry: 'Retailer',
    reportCount: 43,
    users: 4,
    mrr: '$1,200'
  }, {
    id: 5,
    name: 'Elevate Distribution',
    industry: 'Distributor',
    reportCount: 31,
    users: 3,
    mrr: '$1,000'
  }];
  // Bar chart functions from Home.tsx
  const generateBarData = (matches: number) => {
    const days = 30;
    const data = [];
    for (let i = 0; i < days; i++) {
      const value = Math.floor(Math.random() * (matches + 1));
      data.push(value);
    }
    return data;
  };
  const getCategoryColor = (category: string, alertName: string) => {
    // Special case for "Bursting at the Seams Alert"
    if (alertName === 'Bursting at the Seams Alert') {
      return 'bg-yellow-500';
    }
    if (category.startsWith('Upsell')) {
      return 'bg-green-500';
    } else if (category.startsWith('Risk')) {
      return 'bg-red-500';
    } else if (category.startsWith('Action')) {
      return 'bg-yellow-500';
    } else {
      return 'bg-blue-500';
    }
  };
  const renderBarChart = () => {
    const barData = generateBarData(alert.matches);
    const maxValue = Math.max(...barData) || 1;
    const color = getCategoryColor(alert.category, alert.name);
    return;
  };
  // Start editing title
  const handleEditTitle = () => {
    setEditedTitle(alert.name);
    setIsEditingTitle(true);
    setTimeout(() => {
      titleInputRef.current?.focus();
    }, 0);
  };
  // Start editing description
  const handleEditDescription = () => {
    setEditedDescription(alert.description);
    setIsEditingDescription(true);
    setTimeout(() => {
      descriptionInputRef.current?.focus();
    }, 0);
  };
  // Save title changes
  const handleSaveTitle = () => {
    setAlert(prev => ({
      ...prev,
      name: editedTitle
    }));
    setIsEditingTitle(false);
  };
  // Save description changes
  const handleSaveDescription = () => {
    setAlert(prev => ({
      ...prev,
      description: editedDescription
    }));
    setIsEditingDescription(false);
  };
  // Cancel editing
  const handleCancelTitleEdit = () => {
    setIsEditingTitle(false);
  };
  const handleCancelDescriptionEdit = () => {
    setIsEditingDescription(false);
  };
  // Toggle dropdown visibility
  const toggleDropdown = (dropdownId: string) => {
    if (openDropdown === dropdownId) {
      setOpenDropdown(null);
    } else {
      setOpenDropdown(dropdownId);
    }
  };
  // Handle dropdown selection
  const handleDropdownSelection = (filterId: number, field: string, value: string) => {
    setAlert(prev => ({
      ...prev,
      filters: prev.filters.map(filter => filter.id === filterId ? {
        ...filter,
        [field]: value
      } : filter)
    }));
    setOpenDropdown(null);
  };
  // Add new filter
  const handleAddFilter = () => {
    const newFilter = {
      id: alert.filters.length + 1,
      color: 'blue',
      action: 'Select Action',
      condition: 'Count',
      operator: '>',
      value: '0',
      timeframe: '30 days'
    };
    setAlert(prev => ({
      ...prev,
      filters: [...prev.filters, newFilter]
    }));
  };
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  const handleUserClick = (user: any) => {
    navigate(`/users/${user.id}`);
  };
  const handleAccountClick = (account: any) => {
    navigate(`/accounts/${account.id}`);
  };
  const getFilterColorClass = (color: string) => {
    switch (color) {
      case 'blue':
        return 'bg-blue-50 border-blue-200';
      case 'green':
        return 'bg-green-50 border-green-200';
      case 'purple':
        return 'bg-purple-50 border-purple-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };
  return <div className="space-y-4" data-id="element-726">
      <div className="flex items-center gap-4" data-id="element-727">
        <button onClick={() => navigate('/alerts')} className="text-blue-600 hover:text-blue-800 flex items-center" data-id="element-728">
          <ArrowLeft size={16} className="mr-1" data-id="element-729" />
          <span className="text-sm" data-id="element-730">Back to Alerts</span>
        </button>
      </div>

      {/* Alert Title and Description Card */}
      <div className="bg-white border border-gray-200 rounded-md p-4 shadow-sm" data-id="element-731">
        <div className="flex items-center justify-between mb-4" data-id="element-732">
          {isEditingTitle ? <div className="flex items-center gap-2 w-full" data-id="element-733">
              <input ref={titleInputRef} type="text" value={editedTitle} onChange={e => setEditedTitle(e.target.value)} className="text-xl font-medium text-gray-900 border border-blue-300 rounded px-2 py-1 w-full focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-734" />
              <button onClick={handleSaveTitle} className="text-green-600 hover:text-green-800" data-id="element-735">
                <Check size={18} data-id="element-736" />
              </button>
              <button onClick={handleCancelTitleEdit} className="text-red-600 hover:text-red-800" data-id="element-737">
                <X size={18} data-id="element-738" />
              </button>
            </div> : <div onClick={handleEditTitle} className="flex items-center cursor-pointer hover:bg-gray-50 px-2 py-1 rounded" data-id="element-739">
              <h1 className="text-xl font-medium text-gray-900" data-id="element-740">
                {alert.name}
              </h1>
              <ChevronDown size={16} className="ml-2 text-gray-500" data-id="element-741" />
            </div>}
          <div className="flex items-center gap-2" data-id="element-742">
            <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded" data-id="element-743">
              {alert.status}
            </span>
          </div>
        </div>
        {isEditingDescription ? <div className="flex items-start gap-2" data-id="element-744">
            <textarea ref={descriptionInputRef} value={editedDescription} onChange={e => setEditedDescription(e.target.value)} rows={3} className="text-sm text-gray-500 border border-blue-300 rounded px-2 py-1 w-full focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-745" />
            <div className="flex flex-col gap-2" data-id="element-746">
              <button onClick={handleSaveDescription} className="text-green-600 hover:text-green-800" data-id="element-747">
                <Check size={18} data-id="element-748" />
              </button>
              <button onClick={handleCancelDescriptionEdit} className="text-red-600 hover:text-red-800" data-id="element-749">
                <X size={18} data-id="element-750" />
              </button>
            </div>
          </div> : <div onClick={handleEditDescription} className="cursor-pointer hover:bg-gray-50 px-2 py-1 rounded flex items-start" data-id="element-751">
            <p className="text-sm text-gray-500" data-id="element-752">{alert.description}</p>
          </div>}
        {/* Bar chart visualization */}
        {renderBarChart()}
        <div className="flex justify-between items-center mt-3 text-xs" data-id="element-753"></div>
      </div>

      {/* Alert Filters Section */}
      <div className="bg-white border border-gray-200 rounded-md p-4 shadow-sm" ref={dropdownRef} data-id="element-754">
        <h2 className="text-sm font-bold text-gray-900 mb-3" data-id="element-755">
          Show me every customer in our HubSpot who...
        </h2>
        <div className="space-y-2" data-id="element-756">
          {alert.filters.map((filter, index) => <div key={filter.id} className="relative" data-id="element-757">
              <div className={`p-3 ${getFilterColorClass(filter.color)} border rounded-sm`} data-id="element-758">
                <div className="flex items-center gap-2 flex-wrap" data-id="element-759">
                  <div className="relative" data-id="element-760">
                    <button className="px-3 py-1 text-xs bg-white border border-gray-200 rounded flex items-center" onClick={() => toggleDropdown(`action-${filter.id}`)} data-id="element-761">
                      {filter.action}
                      <ChevronDown size={12} className="ml-1 text-gray-500" data-id="element-762" />
                    </button>
                    {openDropdown === `action-${filter.id}` && <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-md shadow-lg z-10" data-id="element-763">
                        <div className="p-2 border-b border-gray-200" data-id="element-764">
                          <div className="font-medium text-sm text-gray-700" data-id="element-765">
                            User Actions
                          </div>
                          <div className="mt-1 max-h-40 overflow-y-auto" data-id="element-766">
                            {userActions.map((action, i) => <div key={i} className="px-2 py-1 text-xs hover:bg-blue-50 cursor-pointer rounded" onClick={() => handleDropdownSelection(filter.id, 'action', action)} data-id="element-767">
                                {action}
                              </div>)}
                          </div>
                        </div>
                        <div className="p-2" data-id="element-768">
                          <div className="font-medium text-sm text-gray-700" data-id="element-769">
                            Entity Types
                          </div>
                          <div className="mt-1" data-id="element-770">
                            <div className="px-2 py-1 text-xs hover:bg-blue-50 cursor-pointer rounded" onClick={() => handleDropdownSelection(filter.id, 'action', 'Account')} data-id="element-771">
                              Account
                            </div>
                            <div className="px-2 py-1 text-xs hover:bg-blue-50 cursor-pointer rounded" onClick={() => handleDropdownSelection(filter.id, 'action', 'User')} data-id="element-772">
                              User
                            </div>
                          </div>
                        </div>
                      </div>}
                  </div>
                  {filter.condition === 'Count' && <>
                      <span className="text-xs text-gray-700" data-id="element-773">count is</span>
                      <div className="relative" data-id="element-774">
                        <button className="px-3 py-1 text-xs bg-white border border-gray-200 rounded flex items-center" onClick={() => toggleDropdown(`operator-${filter.id}`)} data-id="element-775">
                          {filter.operator} {filter.value}
                          <ChevronDown size={12} className="ml-1 text-gray-500" data-id="element-776" />
                        </button>
                        {openDropdown === `operator-${filter.id}` && <div className="absolute top-full left-0 mt-1 w-40 bg-white border border-gray-200 rounded-md shadow-lg z-10" data-id="element-777">
                            <div className="p-2" data-id="element-778">
                              {operators.map((op, i) => <div key={i} className="px-2 py-1 text-xs hover:bg-blue-50 cursor-pointer rounded" onClick={() => handleDropdownSelection(filter.id, 'operator', op)} data-id="element-779">
                                  {op}
                                </div>)}
                            </div>
                          </div>}
                      </div>
                      <span className="text-xs text-gray-700" data-id="element-780">in the last</span>
                      <div className="relative" data-id="element-781">
                        <button className="px-3 py-1 text-xs bg-white border border-gray-200 rounded flex items-center" onClick={() => toggleDropdown(`timeframe-${filter.id}`)} data-id="element-782">
                          {filter.timeframe}
                          <ChevronDown size={12} className="ml-1 text-gray-500" data-id="element-783" />
                        </button>
                        {openDropdown === `timeframe-${filter.id}` && <div className="absolute top-full left-0 mt-1 w-40 bg-white border border-gray-200 rounded-md shadow-lg z-10" data-id="element-784">
                            <div className="p-2" data-id="element-785">
                              {timeframes.map((time, i) => <div key={i} className="px-2 py-1 text-xs hover:bg-blue-50 cursor-pointer rounded" onClick={() => handleDropdownSelection(filter.id, 'timeframe', time)} data-id="element-786">
                                  {time}
                                </div>)}
                            </div>
                          </div>}
                      </div>
                    </>}
                  {filter.condition === 'Property' && <>
                      <span className="text-xs text-gray-700" data-id="element-787">where</span>
                      <div className="relative" data-id="element-788">
                        <button className="px-3 py-1 text-xs bg-white border border-gray-200 rounded flex items-center" onClick={() => toggleDropdown(`property-${filter.id}`)} data-id="element-789">
                          {filter.property}
                          <ChevronDown size={12} className="ml-1 text-gray-500" data-id="element-790" />
                        </button>
                        {openDropdown === `property-${filter.id}` && <div className="absolute top-full left-0 mt-1 w-40 bg-white border border-gray-200 rounded-md shadow-lg z-10" data-id="element-791">
                            <div className="p-2" data-id="element-792">
                              <div className="px-2 py-1 text-xs hover:bg-blue-50 cursor-pointer rounded" onClick={() => handleDropdownSelection(filter.id, 'property', 'plan')} data-id="element-793">
                                plan
                              </div>
                              <div className="px-2 py-1 text-xs hover:bg-blue-50 cursor-pointer rounded" onClick={() => handleDropdownSelection(filter.id, 'property', 'active_users')} data-id="element-794">
                                active_users
                              </div>
                              <div className="px-2 py-1 text-xs hover:bg-blue-50 cursor-pointer rounded" onClick={() => handleDropdownSelection(filter.id, 'property', 'industry')} data-id="element-795">
                                industry
                              </div>
                              <div className="px-2 py-1 text-xs hover:bg-blue-50 cursor-pointer rounded" onClick={() => handleDropdownSelection(filter.id, 'property', 'mrr')} data-id="element-796">
                                mrr
                              </div>
                            </div>
                          </div>}
                      </div>
                      <div className="relative" data-id="element-797">
                        <button className="px-3 py-1 text-xs bg-white border border-gray-200 rounded flex items-center" onClick={() => toggleDropdown(`prop-operator-${filter.id}`)} data-id="element-798">
                          {filter.operator}
                          <ChevronDown size={12} className="ml-1 text-gray-500" data-id="element-799" />
                        </button>
                        {openDropdown === `prop-operator-${filter.id}` && <div className="absolute top-full left-0 mt-1 w-40 bg-white border border-gray-200 rounded-md shadow-lg z-10" data-id="element-800">
                            <div className="p-2" data-id="element-801">
                              {operators.map((op, i) => <div key={i} className="px-2 py-1 text-xs hover:bg-blue-50 cursor-pointer rounded" onClick={() => handleDropdownSelection(filter.id, 'operator', op)} data-id="element-802">
                                  {op}
                                </div>)}
                            </div>
                          </div>}
                      </div>
                      <div className="relative" data-id="element-803">
                        <button className="px-3 py-1 text-xs bg-white border border-gray-200 rounded flex items-center" onClick={() => toggleDropdown(`value-${filter.id}`)} data-id="element-804">
                          {filter.value}
                          <ChevronDown size={12} className="ml-1 text-gray-500" data-id="element-805" />
                        </button>
                        {openDropdown === `value-${filter.id}` && <div className="absolute top-full left-0 mt-1 w-40 bg-white border border-gray-200 rounded-md shadow-lg z-10" data-id="element-806">
                            <div className="p-2" data-id="element-807">
                              <div className="px-2 py-1 text-xs hover:bg-blue-50 cursor-pointer rounded" onClick={() => handleDropdownSelection(filter.id, 'value', 'Standard')} data-id="element-808">
                                Standard
                              </div>
                              <div className="px-2 py-1 text-xs hover:bg-blue-50 cursor-pointer rounded" onClick={() => handleDropdownSelection(filter.id, 'value', 'Premium')} data-id="element-809">
                                Premium
                              </div>
                              <div className="px-2 py-1 text-xs hover:bg-blue-50 cursor-pointer rounded" onClick={() => handleDropdownSelection(filter.id, 'value', 'Enterprise')} data-id="element-810">
                                Enterprise
                              </div>
                              <div className="px-2 py-1 text-xs hover:bg-blue-50 cursor-pointer rounded" onClick={() => handleDropdownSelection(filter.id, 'value', '3')} data-id="element-811">
                                3
                              </div>
                              <div className="px-2 py-1 text-xs hover:bg-blue-50 cursor-pointer rounded" onClick={() => handleDropdownSelection(filter.id, 'value', '5')} data-id="element-812">
                                5
                              </div>
                              <div className="px-2 py-1 text-xs hover:bg-blue-50 cursor-pointer rounded" onClick={() => handleDropdownSelection(filter.id, 'value', '10')} data-id="element-813">
                                10
                              </div>
                            </div>
                          </div>}
                      </div>
                    </>}
                </div>
              </div>
              {index < alert.filters.length - 1 && <div className="flex items-center justify-center my-1" data-id="element-814">
                  <span className="text-xs font-medium text-gray-700" data-id="element-815">AND</span>
                </div>}
            </div>)}
          <div className="flex justify-center mt-3" data-id="element-816">
            <button onClick={handleAddFilter} className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm font-medium" data-id="element-817">
              <Plus size={16} data-id="element-818" />
              <span data-id="element-819">Add Filter</span>
            </button>
          </div>
        </div>
      </div>

      {/* Matches section */}
      <div className="bg-white border border-gray-200 rounded-md shadow-sm" data-id="element-820">
        <div className="border-b border-gray-200" data-id="element-821">
          <nav className="flex" data-id="element-822">
            <button className={`py-2 px-4 text-sm ${activeTab === 'users' ? 'border-b-2 border-blue-600 text-blue-600 font-medium' : 'text-gray-500'}`} onClick={() => setActiveTab('users')} data-id="element-823">
              Users ({userData.length})
            </button>
            <button className={`py-2 px-4 text-sm ${activeTab === 'accounts' ? 'border-b-2 border-blue-600 text-blue-600 font-medium' : 'text-gray-500'}`} onClick={() => setActiveTab('accounts')} data-id="element-824">
              Accounts ({accountData.length})
            </button>
          </nav>
        </div>
        <div className="flex" data-id="element-825">
          <div className="w-3/4 border-r border-gray-200" data-id="element-826">
            {activeTab === 'users' ? <DataTable columns={userColumns} data={userData} onRowClick={handleUserClick} data-id="element-827" /> : <DataTable columns={accountColumns} data={accountData} onRowClick={handleAccountClick} data-id="element-828" />}
          </div>
          <div className="w-1/4 p-4" data-id="element-829">
            <div className="mb-2" data-id="element-830">
              <h3 className="text-sm font-medium text-gray-900" data-id="element-831">
                Activity Trend
              </h3>
              <p className="text-xs text-gray-500" data-id="element-832">Last 30 days</p>
            </div>
            {renderBarChart()}
            <div className="mt-3 text-xs text-center" data-id="element-833">
              <span className="font-medium" data-id="element-834">
                {alert.matches} matching {activeTab}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Browser Preview */}
      <div className="col-span-2" data-id="element-835">
        <div className="bg-white border border-gray-200 rounded-md shadow-sm" data-id="element-836">
          <div className="border-b border-gray-200 px-4 py-3" data-id="element-837">
            <div className="flex justify-between items-center" data-id="element-838">
              <h3 className="text-sm font-medium text-gray-900" data-id="element-839">
                Browser Preview
              </h3>
              <div className="text-xs text-gray-500" data-id="element-840">
                May 10, 2023 • 10:30 AM
              </div>
            </div>
          </div>
          <div className="p-4" data-id="element-841">
            <div className="border border-gray-200 rounded-md overflow-hidden" data-id="element-842">
              {/* Browser chrome */}
              <div className="bg-gray-100 px-3 py-2 flex items-center gap-2 border-b border-gray-200" data-id="element-843">
                <div className="flex gap-1.5" data-id="element-844">
                  <div className="w-3 h-3 rounded-full bg-red-400" data-id="element-845"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400" data-id="element-846"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400" data-id="element-847"></div>
                </div>
                <div className="flex-1 bg-white text-xs text-center py-1 rounded-md text-gray-600 truncate" data-id="element-848">
                  https://salesforce.com/lightning/opportunity
                </div>
              </div>
              {/* Browser content area - replaced with Distru web portal image */}
              <div className="relative bg-white" style={{
              height: '500px'
            }} data-id="element-849">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/rxyErhw1SF7i5F6WP41ksR/Salesforce2BLightning-2791053328.png" alt="Salesforce Lightning Interface" className="w-full h-full object-cover" data-id="element-850" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default AlertDetail;